import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import type { SignificantHazardFormData } from './SignificantHazardForm.schema'

type SignificantHazardFormProps = {
  data: SignificantHazardFormData
  onChange: (data: SignificantHazardFormData) => void
  fieldErrors?: {
    number?: string
    description?: string
    category?: string
    examplesOfHazardousSituations?: string
    associatedActivity?: string
    relatedDangerZone?: string
    clauseReference?: string
  }
}

export function SignificantHazardForm({
  data,
  onChange,
  fieldErrors,
}: SignificantHazardFormProps) {
  const { t } = useTranslation('standardsLibrary')

  return (
    <div className='space-y-4'>
      <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
        <div className='space-y-2'>
          <Label htmlFor='hazard-number'>{t('detail.hazardNumber')}</Label>
          <Input
            id='hazard-number'
            value={data.number}
            onChange={(event) => onChange({ ...data, number: event.target.value })}
            aria-invalid={!!fieldErrors?.number}
          />
          {fieldErrors?.number && (
            <p className='text-sm text-destructive'>{fieldErrors.number}</p>
          )}
        </div>
        <div className='space-y-2'>
          <Label htmlFor='hazard-category'>{t('detail.hazardCategory')}</Label>
          <Input
            id='hazard-category'
            value={data.category}
            onChange={(event) => onChange({ ...data, category: event.target.value })}
            aria-invalid={!!fieldErrors?.category}
          />
          {fieldErrors?.category && (
            <p className='text-sm text-destructive'>{fieldErrors.category}</p>
          )}
        </div>
      </div>
      <div className='space-y-2'>
        <Label htmlFor='hazard-description'>{t('detail.hazardDescription')}</Label>
        <Textarea
          id='hazard-description'
          value={data.description}
          onChange={(event) =>
            onChange({ ...data, description: event.target.value })
          }
          rows={2}
          aria-invalid={!!fieldErrors?.description}
        />
        {fieldErrors?.description && (
          <p className='text-sm text-destructive'>{fieldErrors.description}</p>
        )}
      </div>
      <div className='space-y-2'>
        <Label htmlFor='hazard-examples'>
          {t('detail.hazardExamplesOfHazardousSituations')}
        </Label>
        <Textarea
          id='hazard-examples'
          value={data.examplesOfHazardousSituations}
          onChange={(event) =>
            onChange({
              ...data,
              examplesOfHazardousSituations: event.target.value,
            })
          }
          rows={2}
          aria-invalid={!!fieldErrors?.examplesOfHazardousSituations}
        />
        {fieldErrors?.examplesOfHazardousSituations && (
          <p className='text-sm text-destructive'>
            {fieldErrors.examplesOfHazardousSituations}
          </p>
        )}
      </div>
      <div className='space-y-2'>
        <Label htmlFor='hazard-activity'>
          {t('detail.hazardAssociatedActivity')}
        </Label>
        <Input
          id='hazard-activity'
          value={data.associatedActivity}
          onChange={(event) =>
            onChange({ ...data, associatedActivity: event.target.value })
          }
          aria-invalid={!!fieldErrors?.associatedActivity}
        />
        {fieldErrors?.associatedActivity && (
          <p className='text-sm text-destructive'>
            {fieldErrors.associatedActivity}
          </p>
        )}
      </div>
      <div className='space-y-2'>
        <Label htmlFor='hazard-danger-zone'>
          {t('detail.hazardRelatedDangerZone')}
        </Label>
        <Input
          id='hazard-danger-zone'
          value={data.relatedDangerZone}
          onChange={(event) =>
            onChange({ ...data, relatedDangerZone: event.target.value })
          }
          aria-invalid={!!fieldErrors?.relatedDangerZone}
        />
        {fieldErrors?.relatedDangerZone && (
          <p className='text-sm text-destructive'>
            {fieldErrors.relatedDangerZone}
          </p>
        )}
      </div>
      <div className='space-y-2'>
        <Label htmlFor='hazard-clause-ref'>
          {t('detail.hazardClauseReference')}
        </Label>
        <Input
          id='hazard-clause-ref'
          value={data.clauseReference}
          onChange={(event) =>
            onChange({ ...data, clauseReference: event.target.value })
          }
          aria-invalid={!!fieldErrors?.clauseReference}
        />
        {fieldErrors?.clauseReference && (
          <p className='text-sm text-destructive'>
            {fieldErrors.clauseReference}
          </p>
        )}
      </div>
    </div>
  )
}
